import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Chip,
  Box,
  CircularProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { Category, Assignment, LocationOn, PriorityHigh, Person, Event } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import useComplaint from "../../hooks/useComplaint";
import CustomerComplaintDataGrid from "./components/CustomerComplaintDataGrid";
import ComplaintTimeline from "./components/ComplaintTimeLine";
import useAuth from "../../hooks/useAuth";

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [
    {
      field: "customerComplaintId",
      sort: "ASC",
    },
  ],
};

export default function ComplaintDetails() {
  const {
    getComplaint,
    complaint: data,
    getAllCustomerComplaint,
    customerComplaints,
    paging,
    isLoading,
  } = useComplaint();

  const { user: loginUserDetails } = useAuth();

  const [businessType, setBusinessType] = useState("");

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const { t } = useTranslation();

  const [payload, setPayload] = useState({});

  const { state } = useLocation();

  const { id: complaintId } = state || {};

  const [sortModel, setSortModel] = useState([
    {
      field: "createdAt",
      sort: "desc",
    },
  ]);

  const [selectedRows, setSelectedRows] = useState([]);

  const fetchData = async () => {
    if (complaintId) {
      await getComplaint(complaintId);
    }
  };

  const fetchCustomerComplaintData = async () => {
    console.log(payload, "payload0909");
    if (Object.keys(payload).length != 0) {
      await getAllCustomerComplaint(payload);
    }
  };

  useEffect(() => {
    if (loginUserDetails) {
      setBusinessType(loginUserDetails.businessType);
    }
  }, [loginUserDetails]);

  useEffect(() => {
    fetchData();
  }, [complaintId]);

  useEffect(() => {
    fetchCustomerComplaintData();
  }, [payload]);

  useEffect(() => {
    console.log(complaintId, "complaintId");
    if (complaintId) {
      const payloadValues = {
        ...currentPayload,
        filters: [
          {
            fieldName: "complaintId",
            fieldValue: complaintId,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "complaint",
            entityName: "ComplaintEntity",
          },
        ],
      };
      console.log(payloadValues, "payloadValues");
      setPayload(payloadValues);
    } else {
      setPayload(currentPayload);
    }
  }, [complaintId]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  const [filteredComplaints, setFilteredComplaints] = useState([]);

  useEffect(() => {
    let complaintHistories = customerComplaints.filter((data) =>
      selectedRows.includes(data.customerComplaintId),
    );
    setFilteredComplaints(complaintHistories);
  }, [selectedRows]);

  if (!data)
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
      <CardHeader
        title={`Complaint #${data.complaintNo}`}
        sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #ddd" }}
      />

      <CardContent>
        {/* Complaint Master */}
        <Typography variant="h6" gutterBottom>
          Complaint Master
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography>
              <Category fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
              <b>Category:</b> {data.complaintCategory}
            </Typography>
            <Typography>
              <Assignment fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
              <b>Type:</b> {data.complaintType?.complaintTypeName}
            </Typography>
            <Typography>
              <PriorityHigh fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
              <b>Priority:</b> {data.priority}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>
              <LocationOn fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
              <b>Area:</b> {data.area}
            </Typography>
            <Typography>
              <Person fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
              <b>Created By:</b> {data.createdBy}
            </Typography>
            <Typography>
              <Event fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
              <b>Created At:</b> {data.complaintDate}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            {/* Customer Complaints */}
            <Typography variant="h6" gutterBottom>
              Customer Complaints
            </Typography>

            <CustomerComplaintDataGrid
              complaintId={complaintId}
              fetchData={fetchCustomerComplaintData}
              selectedUser={null}
              setSelectedRows={setSelectedRows}
              isLoadedFromAddComplaintField={false}
              checkboxSelection={true}
              businessType={businessType}
            />
          </Grid>
          <Grid item xs={12}>
            <ComplaintTimeline filteredComplaints={filteredComplaints} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
